/**
 * NA-01 Narrative choreography (INV-CH-NA-01, ENGINE-SPEC §4; changes C-D1-NA01, C-D7-NA01,
 * C-NA01-SHORTFORM).
 *
 * Weighted device events per 10 prose sentences: `beats + 1.6·cascades + 1.2·clause`.
 *   beats     hook opening (1) · broetry (2) · reveal pivots (1 each) · direct-address exercises
 *             (1 each) · closing question / CTA (1)
 *   cascades  sentence-initial runs (≥ 3 identical first tokens, or ≥ 2 short ones opening with
 *             Not/No/Is/Sometimes/Some/Give/Or) · ordinal frames (Step one … Step two …, ≥ 3 in a
 *             section) · single-word fragment volleys
 *   clause    a sentence repeating one bigram three times or more (clause anaphora)
 *
 * Changes from the seed:
 *  - C-D1-NA01 / C-D7-NA01: only the rate ramp remains (the count ramp `cascades + piv + clause`
 *    and the `units < 2.5` gate are gone; that also removes SEED-INVENTORY AMB-05's residual zero
 *    floor). The seed's `min(exercises, 2)` cap is a raw count and is dropped: every exercise counts.
 *  - C-NA01-SHORTFORM: hook, closer and broetry are properties of a short-form piece; they count
 *    only when the document is a single section (doc.sections.length ≤ 1).
 *  - The gate "fewer than two device kinds" is kept: the score is capped at 20 (features.kinds).
 *  - D2: the ramp gets (0, 12) prepended (the seed's zero-guard value).
 *
 * Sentences: prose, non-structural, at least two whitespace words (the seed tokenizer dropped
 * one-word sentences; the fragment-volley pattern reads them from the raw text instead).
 * Events are attributed to the paragraph holding them; the ordinal-frame and closer events, being
 * positional, go to the paragraph of the first match / the last text.
 */
import { REGISTER_PROSE, TOK_WORD } from '../document/types.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons, safeScan } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseParagraphs, proseSentenceList, sectionStartPara } from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

/** The seed's rate ramp with the D2 zero guard 12 prepended. The count ramp is removed (D1). */
const RAMP: Ramp = [
	[0, 12],
	[0.5, 30],
	[1, 50],
	[1.8, 66],
	[3, 80],
	[5, 92],
];

export const KINDS_CAP = 20;
const W_CASCADE = 1.6;
const W_CLAUSE = 1.2;
const W_BROETRY = 2;

const F_HOOK = 1;
const F_CASCADE = 2;
const F_CLAUSE = 3;
const F_PIVOT = 4;
const F_EXERCISE = 5;
const F_CLOSER = 6;
const F_BROETRY = 7;
const F_SEQUENCE = 8;
const F_VOLLEY = 9;

export const na01Def: ChannelDef = {
	id: 'NA-01',
	name: 'Narrative choreography',
	family: 'CHOREO',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'rate',
	features: [
		{ id: 'choreography', label: 'Choreography units per 10 sentences' },
		{ id: 'hook', label: 'Hook opening (short form only)' },
		{ id: 'cascade', label: 'Sentence-initial cascade (×1.6)' },
		{ id: 'clauseAnaphora', label: 'Clause anaphora within one sentence (×1.2)' },
		{ id: 'revealPivot', label: 'Reveal pivot' },
		{ id: 'directAddress', label: 'Direct-address exercise' },
		{ id: 'closer', label: 'Closing question to the reader (short form only)' },
		{ id: 'broetry', label: 'Broetry, one aphorism per paragraph (×2, short form only)' },
		{ id: 'sequenceFrames', label: 'Ordinal sequence frames (×1.6)' },
		{ id: 'fragmentVolley', label: 'Single-word fragment volley (×1.6)' },
	],
	minExposure: 8,
	exposureUnit: 'sentences',
	lang: 'native',
	ramp: RAMP,
	unitScale: 10,
	unitLabel: 'per 10 sentences',
	prior: derivePrior(RAMP, 10, 'sentences', 1.5),
	weight: 1.5,
	nd: 0.2,
	changes: ['C-D1-NA01', 'C-D7-NA01', 'C-NA01-SHORTFORM'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

const TWO_WORDS = /\S\s+\S/;

/** Nearest paragraph with exposure to `p` (searching backwards first). */
function exposed(e: Float64Array, p: number): number {
	if ((e[p] as number) > 0) return p;
	for (let d = 1; d < e.length; d++) {
		if (p - d >= 0 && (e[p - d] as number) > 0) return p - d;
		if (p + d < e.length && (e[p + d] as number) > 0) return p + d;
	}
	return p;
}

/** Paragraph whose character range holds `offset` (paragraphs partition the text). */
function paraAt(starts: Uint32Array, count: number, offset: number): number {
	let lo = 0;
	let hi = count - 1;
	while (lo < hi) {
		const mid = (lo + hi + 1) >> 1;
		if ((starts[mid] as number) <= offset) lo = mid;
		else hi = mid - 1;
	}
	return lo;
}

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = lex().channels['ch-na01'];
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	const summary: string[] = [];
	const shortForm = ctx.frozen ? ctx.frozen.na01ShortForm : doc.sections.length <= 1;

	// --- qualifying sentences (prose, ≥ 2 whitespace words) -----------------------------------------
	const pl = proseSentenceList(doc);
	const S = doc.sentences;
	const qStart: number[] = [];
	const qEnd: number[] = [];
	const qPara: number[] = [];
	const qText: string[] = [];
	const qSent: number[] = [];
	for (let i = 0; i < pl.count; i++) {
		const s = pl.sent[i] as number;
		const a = S.charStart[s] as number;
		const en = S.charEnd[s] as number;
		const text = doc.text.slice(a, en);
		if (!TWO_WORDS.test(text)) continue;
		qStart.push(a);
		qEnd.push(en);
		qPara.push(pl.para[i] as number);
		qText.push(text);
		qSent.push(s);
		e[pl.para[i] as number] = (e[pl.para[i] as number] as number) + 1;
	}
	const n = qText.length;
	const add = (p: number, w: number): void => {
		const q = exposed(e, p);
		k[q] = (k[q] as number) + w;
	};

	// --- hook (short form only) -----------------------------------------------------------------------
	let hook = 0;
	if (shortForm && n > 0) {
		const first = qText[0] as string;
		if (nr.get('hookQuestionEnd').test(first) || nr.get('hookOpener').test(first)) {
			hook = 1;
			ev.add(qStart[0] as number, qEnd[0] as number, F_HOOK, 1);
			add(qPara[0] as number, 1);
			summary.push(`hook opening: “${first.slice(0, 58)}…”`);
		}
	}

	// --- sentence-initial cascades --------------------------------------------------------------------
	const reFirst = nr.get('firstToken');
	const reShort = nr.get('cascadeShortLead');
	const firstTok = qText.map((t) => (reFirst.exec(t) ?? [''])[0]);
	const wsWords = (t: string): number => t.split(/\s+/).length;
	let cascades = 0;
	const cascadeLines: string[] = [];
	for (let i = 0; i < n - 1; ) {
		const t = firstTok[i] as string;
		let j = i + 1;
		while (j < n && t && firstTok[j] === t) j++;
		const run = j - i;
		let shortish = false;
		if (run >= 2) {
			shortish = true;
			for (let q = i; q < j; q++) {
				if (wsWords(qText[q] as string) > 9) {
					shortish = false;
					break;
				}
			}
		}
		if (run >= 3 || (run >= 2 && shortish && reShort.test(t))) {
			cascades++;
			ev.add(qStart[i] as number, qEnd[j - 1] as number, F_CASCADE, W_CASCADE);
			add(qPara[i] as number, W_CASCADE);
			cascadeLines.push(`“${t} …” ×${run}: sentence-initial cascade`);
		}
		i = j > i + 1 ? j : i + 1;
	}

	// --- clause anaphora inside one sentence ----------------------------------------------------------
	const reStop = nr.get('clauseBigramStop');
	const T = doc.tokens;
	let clause = 0;
	const clauseLines: string[] = [];
	const ids: number[] = [];
	const used: number[] = [];
	for (let i = 0; i < n; i++) {
		// word tokens of the sentence (the seed's `[a-z'’]+` words, compared by folded id)
		const s = qSent[i] as number;
		ids.length = 0;
		for (let t = S.tokStart[s] as number; t < (S.tokEnd[s] as number); t++)
			if (T.cls[t] === TOK_WORD) ids.push(T.id[t] as number);
		const len = ids.length;
		if (len < 4) continue;
		used.length = len;
		used.fill(0);
		let top: [number, number, number] | undefined; // first token id, second token id, count
		for (let a = 0; a < len - 1; a++) {
			if (used[a]) continue;
			let c = 1;
			for (let b = a + 1; b < len - 1; b++) {
				if (ids[b] === ids[a] && ids[b + 1] === ids[a + 1]) {
					c++;
					used[b] = 1;
				}
			}
			if (c >= 3 && (!top || c > top[2])) {
				const key = `${T.fold(ids[a] as number)} ${T.fold(ids[a + 1] as number)}`;
				if (!reStop.test(key)) top = [ids[a] as number, ids[a + 1] as number, c];
			}
		}
		if (top) {
			clause++;
			ev.add(qStart[i] as number, qEnd[i] as number, F_CLAUSE, W_CLAUSE);
			add(qPara[i] as number, W_CLAUSE);
			clauseLines.push(
				`“${T.fold(top[0])} ${T.fold(top[1])}” ×${top[2]}: clause anaphora within one sentence`
			);
		}
	}

	// --- per-block scans: ordinal frames, volleys, pivots, exercises ----------------------------------
	const reSeq = nr.get('sequenceFrame');
	const rePivot = nr.get('revealPivot');
	const reAddr = nr.get('directAddress');
	const reVolley = nr.get('fragmentVolley');
	const secOf = sectionStartPara(doc);
	const seqBySection = new Map<number, Array<[number, number, number]>>();
	let volleys = 0;
	let piv = 0;
	let exr = 0;
	for (const p of proseParagraphs(doc)) {
		if (doc.paragraphs.register[p] !== REGISTER_PROSE) continue;
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		const base = b.charStart;
		safeScan(reSeq, text, base, (s, en, m) => {
			const lead = m[0].search(/[A-Za-z]/);
			const sec = secOf[p] as number;
			let arr = seqBySection.get(sec);
			if (!arr) {
				arr = [];
				seqBySection.set(sec, arr);
			}
			arr.push([s + lead, en, p]);
		});
		safeScan(reVolley, text, base, (s, _en, m) => {
			const lead = m[0].search(/[A-Z]/);
			ev.add(s + lead, s + m[0].trimEnd().length, F_VOLLEY, W_CASCADE);
			add(p, W_CASCADE);
			volleys++;
		});
		safeScan(rePivot, text, base, (s, en) => {
			ev.add(s, en, F_PIVOT, 1);
			add(p, 1);
			piv++;
		});
		safeScan(reAddr, text, base, (s, en) => {
			ev.add(s, en, F_EXERCISE, 1);
			add(p, 1);
			exr++;
		});
	}
	let seqFrames = 0;
	for (const arr of seqBySection.values()) {
		if (arr.length < 3) continue;
		seqFrames++;
		arr.forEach(([s, en], q) => ev.add(s, en, F_SEQUENCE, q === 0 ? W_CASCADE : 0));
		add((arr[0] as [number, number, number])[2], W_CASCADE);
		cascadeLines.push(`sequence frames ×${arr.length}: ordinal anaphora across the section`);
	}
	cascades += seqFrames + volleys;
	if (volleys > 0)
		cascadeLines.push(
			`${volleys} fragment volley${volleys === 1 ? '' : 's'}: single-word sentence cascade (“Guilt. Shame.”)`
		);

	// --- broetry and closer (short form only) ---------------------------------------------------------
	let broetry = 0;
	let closer = 0;
	if (shortForm) {
		const prose = proseParagraphs(doc);
		let words = 0;
		for (const p of prose) words += doc.paragraphs.ownWords[p] as number;
		if (prose.length >= 8 && n > 0) {
			const ratio = prose.length / n;
			const mean = words / prose.length;
			if (ratio >= 0.75 && mean <= 30) {
				broetry = 1;
				const singles = prose.filter((p) => (e[p] as number) === 1);
				const carriers = singles.length > 0 ? singles : [prose[0] as number];
				const w = W_BROETRY / carriers.length;
				for (const p of carriers) {
					const b = doc.blocks[doc.paragraphs.block[p] as number];
					if (b) ev.add(b.charStart, b.charEnd, F_BROETRY, w);
					k[p] = (k[p] as number) + w;
				}
				summary.push(
					`${prose.length} single-sentence paragraphs: broetry format, one aphorism per line`
				);
			}
		}
		const end = doc.text.trimEnd().length;
		const tailAt = Math.max(0, end - 300);
		const tail = doc.text.slice(tailAt, end);
		const m =
			nr.get('closerQuestion').exec(tail) ??
			nr.get('closerCta').exec(tail) ??
			nr.get('closerToEveryone').exec(tail) ??
			nr.get('closerEmoji').exec(tail);
		if (m && P > 0) {
			closer = 1;
			const at = tailAt + m.index;
			ev.add(at, tailAt + m.index + m[0].length, F_CLOSER, 1);
			add(paraAt(doc.paragraphs.charStart, P, at), 1);
			summary.push('closing question to reader: engagement CTA ending');
		}
	}

	const kinds =
		hook +
		Math.min(cascades, 2) +
		(clause > 0 ? 1 : 0) +
		(piv > 0 ? 1 : 0) +
		(exr > 0 ? 1 : 0) +
		closer +
		broetry;
	const beats = hook + W_BROETRY * broetry + piv + exr + closer;
	const units = beats + W_CASCADE * cascades + W_CLAUSE * clause;
	for (const l of cascadeLines) if (summary.length < 8) summary.push(l);
	for (const l of clauseLines) if (summary.length < 8) summary.push(l);
	if (piv)
		summary.push(
			`${piv} reveal pivot${piv === 1 ? '' : 's'}: “here is the part people miss” family`
		);
	if (exr)
		summary.push(
			`${exr} direct-address exercise${exr === 1 ? '' : 's'}: “Ask yourself…” coaching beat`
		);
	summary.push(
		`${fmt(units, 1)} choreography units · ${cascades} cascades · ${piv} pivots · ${kinds} device types`
	);
	return {
		k,
		e,
		evidence: ev,
		notes: shortForm ? [] : ['hook, closer and broetry not counted: multi-section document'],
		features: {
			units,
			beats,
			cascades,
			clause,
			pivots: piv,
			exercises: exr,
			hook,
			closer,
			broetry,
			kinds,
			shortForm: shortForm ? 1 : 0,
		},
		summary,
	};
}

const base: ChannelImpl = rateChannel(na01Def, measure);

/** The seed's device-kind gate, kept: fewer than two kinds of device caps the score at 20. */
export const na01: ChannelImpl = {
	def: base.def,
	phase: base.phase,
	measure: base.measure,
	run(ctx: RunContext): ChannelResult {
		const r = base.run(ctx);
		if (r.status === 'scored' && (r.features.kinds ?? 0) < 2) {
			r.doc.score = Math.min(KINDS_CAP, r.doc.score);
			r.doc.scoreCi90 = [
				Math.min(KINDS_CAP, r.doc.scoreCi90[0]),
				Math.min(KINDS_CAP, r.doc.scoreCi90[1]),
			];
		}
		return r;
	},
};
export { na01Def as def, na01 as impl };
